<template>
    <app-layout title="Survey Management">
        <template v-slot:header>
            <div class="flex justify-between">
                <h2 class="font-bold text-4xl dark:text-gray-200">
                    My diagnostics
                </h2>
                <inertia-link v-if="canManageSurveys && tenant"
                              :href="route('tenant.survey.create', tenant.id)"
                >
                    <jet-button :has-icon="true">Create survey</jet-button>
                </inertia-link>
                <jet-select class="w-1/3 rounded-md" v-if="organizations && $page.props.user.isSuperAdmin" v-model:selected="selectedOrg">
                    <option :value="null">All Organizations</option>
                    <option v-for="(orgName, orgId) in organizations" :key="'org-' + orgId" :value="orgId">
                        {{ orgName }}
                    </option>
                </jet-select>
            </div>
        </template>

        <div class="p-6 shadow-md rounded-md">
            <template v-if="surveys.length">
                <survey-card v-for="survey in surveys">
                    <template v-slot:data>
                        <div>
                            <p class="text-2xl leading-6 font-bold mb-2">{{ survey.name }}</p>
                            <p>Created: {{ moment(survey.created_at).format('DD-MM-YYYY HH:mm') }}</p>
                            <p v-if="!tenant">
                                {{ survey.tenant ? survey.tenant.name : '' }}
                            </p>
                        </div>
                        <div>
                            <p>Started: {{ survey.respondentStats.invitedStarted }}/{{ survey.respondentStats.invitedTotal }} invited, {{ survey.respondentStats.additionalStarted }} additional</p>
                            <p>Finished: {{ survey.respondentStats.invitedFinished }}/{{ survey.respondentStats.invitedTotal }} invited, {{ survey.respondentStats.additionalFinished }} additional</p>
                            <p class="font-bold">Respondent slots: {{ survey.respondentStats.startedTotal }} used/{{ survey.paid_responses }} purchased, {{ survey.respondentStats.availableResponses }} available</p>
                        </div>
                        <div class="xl:text-right">
                            <p class="text-2xl">{{ survey.survey_type.type }}</p>
                        </div>
                    </template>
                    <template v-slot:actions>
                        <pill :type="pillColorsByStatus[survey.status]">
                            {{ survey.current_status }}
                            <template v-if="survey.status === 1">
                                for {{ moment(survey.live_at).format("DD-MM-YYYY HH:mm") }}
                            </template>
                        </pill>

                        <inertia-link
                            :href="route('tenant.survey.preview', {survey: survey.id})"
                            title="Preview Survey"
                            class="flex items-center font-bold text-lg leading-6"
                            aria-label="Show"
                        >
                            <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                <path d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"/>
                            </svg>
                            Preview Survey
                        </inertia-link>
                        <template v-if="survey.isActive">
                            <button title="Copy survey URL"
                                    @click.prevent="copyToClipboard(survey)"
                                    class="flex items-center font-bold text-lg leading-6">
                                <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                    <path d="M320 448v40c0 13.255-10.745 24-24 24H24c-13.255 0-24-10.745-24-24V120c0-13.255 10.745-24 24-24h72v296c0 30.879 25.121 56 56 56h168zm0-344V0H152c-13.255 0-24 10.745-24 24v368c0 13.255 10.745 24 24 24h272c13.255 0 24-10.745 24-24V128H344c-13.2 0-24-10.8-24-24zm120.971-31.029L375.029 7.029A24 24 0 0 0 358.059 0H352v96h96v-6.059a24 24 0 0 0-7.029-16.97z"/>
                                </svg>
                                Copy survey URL
                            </button>
                        </template>
                        <inertia-link v-if="survey.status !== 0"
                                      :href="route('tenant.survey.show', {survey: survey.id})"
                                      title="Survey details"
                                      class="flex items-center font-bold text-lg leading-6"
                                      aria-label="Survey details"
                        >
                            <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                                <path d="M336 64h-80c0-35.3-28.7-64-64-64s-64 28.7-64 64H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM96 424c-13.3 0-24-10.7-24-24s10.7-24 24-24 24 10.7 24 24-10.7 24-24 24zm0-96c-13.3 0-24-10.7-24-24s10.7-24 24-24 24 10.7 24 24-10.7 24-24 24zm0-96c-13.3 0-24-10.7-24-24s10.7-24 24-24 24 10.7 24 24-10.7 24-24 24zm96-192c13.3 0 24 10.7 24 24s-10.7 24-24 24-24-10.7-24-24 10.7-24 24-24zm128 368c0 4.4-3.6 8-8 8H168c-4.4 0-8-3.6-8-8v-16c0-4.4 3.6-8 8-8h144c4.4 0 8 3.6 8 8v16zm0-96c0 4.4-3.6 8-8 8H168c-4.4 0-8-3.6-8-8v-16c0-4.4 3.6-8 8-8h144c4.4 0 8 3.6 8 8v16zm0-96c0 4.4-3.6 8-8 8H168c-4.4 0-8-3.6-8-8v-16c0-4.4 3.6-8 8-8h144c4.4 0 8 3.6 8 8v16z"/>
                            </svg>
                            Survey details
                        </inertia-link>
                        <inertia-link v-if="canManageSurveys && tenant && survey.status === 0"
                                      :href="route('tenant.survey.edit', {tenant: survey.tenant_id, survey: survey.id})"
                                      title="Edit Survey"
                                      class="flex items-center font-bold text-lg leading-6"
                                      aria-label="Edit Survey"
                        >
                            <svg class="mr-2 w-6 h-6" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                            </svg>
                            Edit Survey
                        </inertia-link>
                        <button v-if="canManageSurveys && tenant && canBeClosed(survey)"
                                @click.prevent="closeSurvey(survey)"
                                title="Close survey"
                                class="flex items-center font-bold text-lg leading-6"
                                aria-label="Close survey"
                        >
                            <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 352 512">
                                <path d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"/>
                            </svg>
                            Close Survey
                        </button>
                        <button v-else-if="canManageSurveys && tenant && canBeReopened(survey)"
                                @click.prevent="reopenSurvey(survey)"
                                title="Reopen survey"
                                class="flex items-center font-bold text-lg leading-6"
                                aria-label="Reopen survey"
                        >
                            <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                <path d="M504 255.531c.253 136.64-111.18 248.372-247.82 248.468-59.015.042-113.223-20.53-155.822-54.911-11.077-8.94-11.905-25.541-1.839-35.607l11.267-11.267c8.609-8.609 22.353-9.551 31.891-1.984C173.062 425.135 212.781 440 256 440c101.705 0 184-82.311 184-184 0-101.705-82.311-184-184-184-48.814 0-93.149 18.969-126.068 49.932l50.754 50.754c10.08 10.08 2.941 27.314-11.313 27.314H24c-8.837 0-16-7.163-16-16V38.627c0-14.254 17.234-21.393 27.314-11.314l49.372 49.372C129.209 34.136 189.552 8 256 8c136.81 0 247.747 110.78 248 247.531zm-180.912 78.784l9.823-12.63c8.138-10.463 6.253-25.542-4.21-33.679L288 256.349V152c0-13.255-10.745-24-24-24h-16c-13.255 0-24 10.745-24 24v135.651l65.409 50.874c10.463 8.137 25.541 6.253 33.679-4.21z"/>
                            </svg>
                            Reopen survey
                        </button>
                        <inertia-link v-if="tenant && canDownloadReport(survey)"
                                      :href="route('tenant.survey.download-report', {tenant: survey.tenant_id, survey: survey.id})"
                                      title="Download report"
                                      class="flex items-center font-bold text-lg leading-6"
                                      aria-label="Download report"
                        >
                            <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                                <path d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm76.45 211.36l-96.42 95.7c-6.65 6.61-17.39 6.61-24.04 0l-96.42-95.7C73.42 337.29 80.54 320 94.82 320H160v-80c0-8.84 7.16-16 16-16h32c8.84 0 16 7.16 16 16v80h65.18c14.28 0 21.4 17.29 11.27 27.36zM377 105L279.1 7c-4.5-4.5-10.6-7-17-7H256v128h128v-6.1c0-6.3-2.5-12.4-7-16.9z"/>
                            </svg>
                            Download report
                        </inertia-link>
                        <inertia-link v-if="canManageSurveys && tenant && survey.status !== 0"
                                      :href="route('tenant.stripe.create-order', {tenant: survey.tenant_id, survey: survey.id})"
                                      title="Buy credits"
                                      class="flex items-center font-bold text-lg leading-6"
                                      aria-label="Buy credits"
                        >
                            <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                                <path d="M528.12 301.319l47.273-208C578.806 78.301 567.391 64 551.99 64H159.208l-9.166-44.81C147.758 8.021 137.93 0 126.529 0H24C10.745 0 0 10.745 0 24v16c0 13.255 10.745 24 24 24h69.883l70.248 343.435C147.325 417.1 136 435.222 136 456c0 30.928 25.072 56 56 56s56-25.072 56-56c0-15.674-6.447-29.835-16.824-40h209.647C430.447 426.165 424 440.326 424 456c0 30.928 25.072 56 56 56s56-25.072 56-56c0-22.172-12.888-41.332-31.579-50.405l5.517-24.276c3.413-15.018-8.002-29.319-23.403-29.319H218.117l-6.545-32h293.145c11.206 0 20.92-7.754 23.403-18.681z"/>
                            </svg>
                            Buy credits
                        </inertia-link>
                        <button v-if="canManageSurveys && tenant && hasUnfinishedOrder(survey)"
                                @click.prevent="finalizeOrder(survey)"
                                title="Finalize order"
                                class="flex items-center font-bold text-lg leading-6"
                                aria-label="Finalize order"
                        >
                            <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                                <path d="M336 64h-80c0-35.3-28.7-64-64-64s-64 28.7-64 64H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM192 40c13.3 0 24 10.7 24 24s-10.7 24-24 24-24-10.7-24-24 10.7-24 24-24zm121.2 231.8l-143 141.8c-4.7 4.7-12.3 4.6-17-.1l-82.6-83.3c-4.7-4.7-4.6-12.3.1-17L99.1 285c4.7-4.7 12.3-4.6 17 .1l46 46.4 106-105.2c4.7-4.7 12.3-4.6 17 .1l28.2 28.4c4.7 4.8 4.6 12.3-.1 17z"/>
                            </svg>
                            Finalize order
                        </button>
                        <button v-if="canManageSurveys && tenant"
                                @click.prevent="deleteSurvey(survey)"
                                title="Delete survey"
                                class="flex items-center font-bold text-lg leading-6"
                                aria-label="Delete survey"
                        >
                            <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                <path d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"/>
                            </svg>
                            Delete survey
                        </button>
                    </template>
                </survey-card>
                <table-pagination :pagination-object="paginatedSurveys"/>
            </template>

            <template v-else>
                <p class="text-2xl font-bold leading-6 text-center">No surveys</p>
            </template>

        </div>

        <!--<div class="w-full overflow-hidden rounded-lg shadow mt-10">
            <table-layout>
                <template v-slot:theadRow>
                    <th class="px-4 py-3">Name</th>
                    <th class="px-4 py-3">Type</th>
                    <th class="px-4 py-3">Status</th>
                    <th class="px-4 py-3">Respondents</th>
                    <th class="px-4 py-3">Actions</th>
                </template>
                <template v-slot:tbody v-if="surveys.length">
                    <tr v-for="survey in surveys" class="text-gray-700 dark:text-gray-400">
                        <td class="px-4 py-3">
                            <div class="flex items-center text-sm">
                                <div>
                                    <p class="font-semibold">{{ survey.name }}</p>
                                    <p class="text-xs text-gray-600 dark:text-gray-400">
                                        {{ moment(survey.created_at).format("DD-MM-YYYY HH:mm") }}
                                    </p>
                                    <p v-if="!tenant" class="text-xs text-gray-600 dark:text-gray-400">
                                        {{ survey.tenant ? survey.tenant.name : '' }}
                                    </p>
                                </div>
                            </div>
                        </td>
                        <td class="px-4 py-3">
                            <div class="flex items-center text-sm">
                                <div>
                                    {{ survey.survey_type.type }}
                                </div>
                            </div>
                        </td>
                        <td class="px-4 py-3 text-xs">
                            <div class="flex items-center">
                                <pill :type="pillColorsByStatus[survey.status]">
                                    {{ survey.current_status }}
                                    <template v-if="survey.status === 1">
                                        for {{ moment(survey.live_at).format("DD-MM-YYYY HH:mm") }}
                                    </template>
                                </pill>
                                <inertia-link
                                    :href="route('tenant.survey.preview', {survey: survey.id})"
                                    title="Preview Survey"
                                    class="flex items-center justify-between px-1 ml-1 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                    aria-label="Show"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-purple-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z" />
                                    </svg>
                                </inertia-link>
                                <template v-if="survey.isActive">
                                    <button title="Copy survey URL"
                                            @click.prevent="copyToClipboard(survey)"
                                            class="px-1">
                                        <svg  xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-purple-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                        </svg>
                                    </button>
                                </template>
                            </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                            <p class="text-xs text-gray-600 dark:text-gray-400">
                                Started: {{ survey.respondentStats.invitedStarted }}/{{ survey.respondentStats.invitedTotal }} invited, {{ survey.respondentStats.additionalStarted }} additional
                            </p>
                            <p class="text-xs text-gray-600 dark:text-gray-400">
                                Finished: {{ survey.respondentStats.invitedFinished }}/{{ survey.respondentStats.invitedTotal }} invited, {{ survey.respondentStats.additionalFinished }} additional
                            </p>
                            <p class="text-xs text-gray-600 dark:text-gray-400 font-bold">
                                Respondent slots: {{ survey.respondentStats.startedTotal }} used/{{ survey.paid_responses }} purchased, {{ survey.respondentStats.availableResponses }} available
                            </p>
                        </td>
                        <td class="px-4 py-3">
                            <div class="flex items-center space-x-1 text-sm">
                                <inertia-link v-if="survey.status !== 0"
                                    :href="route('tenant.survey.show', {survey: survey.id})"
                                    title="Survey details"
                                    class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                    aria-label="Survey details"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg"
                                         class="h-5 w-5 text-purple-600 dark:text-gray-400" fill="none"
                                         viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                    </svg>
                                </inertia-link>
                                <inertia-link v-if="canManageSurveys && tenant && survey.status === 0"
                                              :href="route('tenant.survey.edit', {tenant: survey.tenant_id, survey: survey.id})"
                                              title="Edit survey"
                                              class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                              aria-label="Edit survey"
                                >
                                    <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                        <path
                                            d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                                    </svg>
                                </inertia-link>
                                &lt;!&ndash; <inertia-link v-else-if="canSeeAnalytics(survey)"
                                              :href="route('tenant.survey.analytics', {survey: survey.id})"
                                              title="Analytics"
                                              class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                              aria-label="Analytics"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none"
                                         viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                                    </svg>
                                </inertia-link> &ndash;&gt;
                                <button v-if="canManageSurveys && tenant && canBeClosed(survey)"
                                        @click.prevent="closeSurvey(survey)"
                                        title="Close survey"
                                        class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                        aria-label="Close survey"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                    </svg>
                                    &lt;!&ndash;                                    <font-awesome-icon :size="'lg'" :icon="['fas', 'clipboard-check']"></font-awesome-icon>&ndash;&gt;
                                </button>
                                <button v-else-if="canManageSurveys && tenant && canBeReopened(survey)"
                                        @click.prevent="reopenSurvey(survey)"
                                        title="Reopen survey"
                                        class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                        aria-label="Reopen survey"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                    </svg>
                                </button>
                                <a v-if="tenant && canDownloadReport(survey)"
                                   :href="route('tenant.survey.download-report', {tenant: survey.tenant_id, survey: survey.id})"
                                   title="Download report"
                                   class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                   aria-label="Download report"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    &lt;!&ndash;                                    <font-awesome-icon :size="'lg'" :icon="['fas', 'clipboard-check']"></font-awesome-icon>&ndash;&gt;
                                </a>
                                <button v-if="canManageSurveys && tenant && hasUnfinishedOrder(survey)"
                                        @click.prevent="finalizeOrder(survey)"
                                        title="Finalize order"
                                        class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                        aria-label="Finalize order"
                                >
                                    <font-awesome-icon :size="'lg'" :icon="['fab', 'stripe']"></font-awesome-icon>
                                </button>
                                <inertia-link v-if="canManageSurveys && tenant && survey.status !== 0"
                                              :href="route('tenant.stripe.create-order', {tenant: survey.tenant_id, survey: survey.id})"
                                              title="Buy additional respondent slots"
                                              class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                              aria-label="Buy additional respondent slots"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </inertia-link>
                                <button v-if="canManageSurveys && tenant"
                                        @click.prevent="deleteSurvey(survey)"
                                        title="Delete survey"
                                        class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                        aria-label="Delete survey"
                                >
                                    <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd"
                                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                              clip-rule="evenodd"></path>
                                    </svg>
                                </button>
                            </div>
                        </td>
                    </tr>
                </template>
                <template v-slot:tbody v-else>
                    <tr class="text-center text-gray-700 dark:text-gray-400">
                        <td colspan="5">
                            No surveys
                        </td>
                    </tr>

                </template>
            </table-layout>
            <table-pagination :pagination-object="paginatedSurveys"/>
        </div>-->
    </app-layout>
</template>

<script>
import {computed, defineComponent, ref, watch} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue';
import SurveyCard from "@/Components/SurveyCard";
import TablePagination from "@/Jetstream/TablePagination";
import TableLayout from "@/Jetstream/TableLayout";
import JetSelect from "@/Jetstream/Select";
import Pill from "@/Jetstream/Pill";
import JetButton from "@/Jetstream/Button";
import {Inertia} from '@inertiajs/inertia';
import {inject} from 'vue'
import {usePage} from "@inertiajs/inertia-vue3";
import useClipboard from "vue-clipboard3";

export default defineComponent({
    components: {
        Pill,
        TableLayout,
        TablePagination,
        AppLayout,
        SurveyCard,
        JetSelect,
        JetButton,
    },
    props: {
        paginatedSurveys: Object,
        surveySettings: {
            type: Object,
            required: true,
        },
        organizations: {
            type: Object,
            default: {},
        }
    },
    setup(props) {
        const canManageSurveys = usePage().props.value.allUserPermissions
            && usePage().props.value.allUserPermissions.indexOf('manage surveys') !== -1;

        const tenant = usePage().props.value.tenant;

        const pillColorsByStatus = [
            'danger', //draft 0
            'warning', //scheduled 1
            'success', //live 2
            'danger', //no respondent slots 3
            'info', //closed 4
            'success', //reopened 5
        ];
        const $swal = inject('$swal');

        const deleteSurvey = (survey) => {
            $swal({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => {
                if (result.isConfirmed) {
                    Inertia.delete(route('tenant.survey.destroy', {tenant: survey.tenant_id, survey: survey.id}));
                }
            });
        };

        const closeSurvey = (survey) => {
            $swal({
                title: 'Are you sure you want to close this survey?',
                text: "Respondents will not be able to resume filling and no new respondents will be allowed",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, close it!'
            }).then((result) => {
                if (result.isConfirmed) {
                    Inertia.put(route('tenant.survey.close', {tenant: survey.tenant_id, survey: survey.id}));
                }
            });
        };

        const reopenSurvey = (survey) => {
            $swal({
                title: 'Are you sure you want to reopen this survey?',
                text: "Respondents will be able to resume filling and new respondents will be allowed",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, reopen it!'
            }).then((result) => {
                if (result.isConfirmed) {
                    Inertia.put(route('tenant.survey.reopen', {tenant: survey.tenant_id, survey: survey.id}));
                }
            });
        };

        // const canSeeAnalytics = (survey) => {
        //     return canManageSurveys
        //         && (
        //             (survey.isFullyCreated && survey.survey_type_id !== 1)
        //             || (survey.isClosed && survey.survey_type_id === 1)
        //         )
        // };

        const canBeClosed = (survey) => {
            return canManageSurveys && !survey.isClosed && survey.isFullyCreated;
        };

        const canBeReopened = (survey) => {
            return props.surveySettings[survey.survey_type_id].reopen && canManageSurveys && survey.isClosed;
        };

        const surveys = computed(() => {
            return props.paginatedSurveys.data.map((survey) => {
                survey.respondentStats = {};
                survey.respondentStats.invitedTotal = survey.respondents
                    .filter((respondent) => respondent.invited).length;
                survey.respondentStats.invitedStarted = survey.respondents
                    .filter((respondent) => respondent.invited && respondent.started_at).length;
                survey.respondentStats.invitedFinished = survey.respondents
                    .filter((respondent) => respondent.invited && respondent.finished_at).length;
                survey.respondentStats.additionalStarted = survey.respondents
                    .filter((respondent) => !respondent.invited && respondent.started_at).length;
                survey.respondentStats.additionalFinished = survey.respondents
                    .filter((respondent) => !respondent.invited && respondent.finished_at).length;
                survey.respondentStats.startedTotal = survey.respondents
                    .filter((respondent) => respondent.started_at).length;
                survey.respondentStats.availableResponses = survey.paid_responses > survey.respondentStats.startedTotal
                    ? survey.paid_responses - survey.respondentStats.startedTotal : 0;
                return survey;
            })
        });

        const {toClipboard} = useClipboard();

        const $toast = inject('$toast');

        const copyToClipboard = async (survey) => {
            let urlString;
            try {
                if (survey.custom_url) {
                    urlString = route('tenant.survey.fill.custom_url', {
                        tenant: survey.tenant_id,
                        customUrl: survey.custom_url,
                    });
                } else {
                    urlString = route('tenant.survey.fill.token', {
                        tenant: survey.tenant_id,
                        token: survey.token,
                    });
                }
                await toClipboard(urlString);
                $toast('Copied to clipboard', {type: "success"});
            } catch (e) {
                console.error(e)
            }
        };

        const hasUnfinishedOrder = (survey) => {
            return survey.latest_order
                && survey.latest_order.status === 0;
        };

        const finalizeOrder = (survey) => {
            Inertia.visit(route('tenant.stripe.finalize-order', {
                tenant: survey.tenant_id,
                survey: survey.id,
                order: survey.latest_order.id
            }));
        }

        const canDownloadReport = (survey) => {
            return canManageSurveys
                && survey.isClosed && props.surveySettings[survey.survey_type_id].anytime_report;
        };

        const selectedOrg = ref(usePage().props.value.orgId);

        const filterByTenant = () => {
            Inertia.visit(route('admin.survey.index', {orgId: selectedOrg.value}));
        }

        watch(selectedOrg, filterByTenant);

        return {
            pillColorsByStatus,
            canManageSurveys,
            deleteSurvey,
            tenant,
            // canSeeAnalytics,
            surveys,
            closeSurvey,
            canBeClosed,
            canBeReopened,
            reopenSurvey,
            copyToClipboard,
            hasUnfinishedOrder,
            finalizeOrder,
            canDownloadReport,
            selectedOrg,
        };
    }
})
</script>
